import ApiService from "@/core/services/ApiService";
import JwtService from "@/core/services/JwtService";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import store from "@/store";
import { Module, Action, Mutation, VuexModule } from "vuex-module-decorators";
import type {
  Credentials,
  IFrameCredentials,
} from "@/core/services/api/interfaces/auth/Credentials";
import UserService, { StorageKey } from "@/core/services/UserService";
import { User } from "@/core/services/api/interfaces/manager/User";
import { BearerToken } from "@/core/services/api/interfaces/auth/BearerToken";
import { UserRole } from "@/core/services/api/enums/UserRole";
import ChangePassword from "@/core/services/api/interfaces/auth/ChangePassword";
import StorageService from "@/core/services/StorageService";
import { getDashboardRoute } from "@/core/services/UserRoleService";
import router from "@/router";
import { RouteLocationRaw } from "vue-router";
import { Company } from "@/core/services/api/interfaces/common/Company";
import {
  EntityPortalService,
  PortalService,
} from "@/core/services/api/interfaces/dict/PortalService";
import { Registration } from "@/core/services/api/interfaces/auth/Registration";
import { ForgotPassword } from "@/core/services/api/interfaces/auth/ForgotPassword";
import Swal, { SweetAlertOptions, SweetAlertResult } from "sweetalert2";
import { LegalType } from "@/core/services/api/interfaces/common/LegalType";
import { CompanyType } from "@/core/services/api/interfaces/common/CompanyType";
import { LiveChatSettings } from "@/interfaces/common/LiveChatSettings";
import { FeatureEnum } from "@/core/services/api/enums/Common";
import { CompanyAlert } from "@/interfaces/common/CompanyAlerts";

const timeout = 120; //seconds

const swal = Swal.mixin({
  icon: "question",
  position: "center",
  showConfirmButton: true,
  showCloseButton: true,
  timerProgressBar: true,
  backdrop: true,
} as SweetAlertOptions) as typeof Swal;

export interface UserAuthInfo {
  errors: unknown;
  user: null | User;
  bearerToken: null | BearerToken;
  adminBearerToken: null | BearerToken;
}

export interface LoginResponse {
  token: BearerToken;
  user: User;
  redirect_url: string | null;
}

const refreshLimit = 10 * 60 * 1000; //10 minutes

@Module
export default class UserAuthModule extends VuexModule implements UserAuthInfo {
  errors = {};
  user = UserService.getUser() as null | User;
  bearerToken = JwtService.getToken() as null | BearerToken;
  adminBearerToken = JwtService.getAdminToken() as null | BearerToken;
  company_alerts: CompanyAlert[] = [];

  authExpiresInterval: number | null = null;

  get isExpired(): boolean {
    if (!this.getBearerToken?.expires_date) {
      return true;
    }

    return new Date(this.getBearerToken.expires_date) < new Date();
  }

  get getAuthExpiresInterval(): number | null {
    return this.authExpiresInterval;
  }

  get getCompanyAlerts(): CompanyAlert[] {
    return this.company_alerts;
  }

  get bearerTokenExpiresDate(): Date | null {
    if (!this.getBearerToken?.expires_date) {
      return null;
    }

    return new Date(this.getBearerToken.expires_date);
  }

  get getBearerToken(): null | BearerToken {
    return this.bearerToken;
  }

  get getBearerTokenString(): null | string {
    return this.getBearerToken?.access_token || null;
  }

  get getBearerTokenFromStorage(): null | BearerToken {
    return JwtService.getToken();
  }

  get getAdminBearerToken(): null | BearerToken {
    return this.adminBearerToken;
  }

  get userPortalServices(): { [index: string]: EntityPortalService } {
    return this.user?.portal_services || {};
  }

  get isUserAuthenticated(): boolean {
    return !this.isExpired;
  }

  get isTokenRefreshable(): boolean {
    if (!this.bearerToken?.expires_date) {
      return false;
    }

    return !this.isExpired;
  }

  get isTokenRequiresRefresh(): boolean {
    if (!this.bearerToken?.expires_date) {
      return false;
    }

    return (
      new Date(this.bearerToken.expires_date).getTime() - Date.now() <
      refreshLimit
    );
  }

  get getUser(): User | null {
    return this.user;
  }

  get getUserLiveChatSettings(): LiveChatSettings {
    return LiveChatSettings.fromUser(this.context.getters.getUser);
  }

  get getUserCompany(): Company | null {
    return this.user?.company || null;
  }

  get isCompanyCryptoEnabled(): boolean {
    return this.isCompanyFeatureEnabled(FeatureEnum.CRYPTO);
  }

  get isCompanyEnabled(): boolean {
    return !!this.getUserCompany?.enabled;
  }

  get isCompanyFeatureEnabled() {
    return (feature: FeatureEnum): boolean => {
      return !!this.getUserCompany?.enabled_features.includes(feature);
    };
  }

  get isCompanyFeaturePspPortalEnabled(): boolean {
    return this.isCompanyFeatureEnabled(FeatureEnum.PSP_PORTAL);
  }

  get isCompanyFeaturePortalsEnabled(): boolean {
    return this.isCompanyFeatureEnabled(FeatureEnum.PORTALS);
  }

  get isCompanyFeatureCreateAccountsEnabled(): boolean {
    return this.isCompanyFeatureEnabled(FeatureEnum.CREATE_ACCOUNTS);
  }

  get isCompanyPspPortalAttached(): boolean {
    return (
      this.isCompanyFeaturePspPortalEnabled &&
      !!this.getUserCompany?.psp_portal_attached
    );
  }

  get isCompanyFeatureAffiliationEnabled(): boolean {
    return this.isCompanyFeatureEnabled(FeatureEnum.AFFILIATION);
  }

  get isCompanyWhiteLabelEnabled(): boolean {
    return !!this.getUserCompany?.white_label_enabled;
  }

  get isUserComplianceTrackingRequired(): boolean {
    return !!(
      this.user?.is_compliance_trackings_required &&
      this.user?.user_lead_id &&
      !this.isLimitedFeaturesFlexPsp
    );
  }

  get isUserComplianceTrackingPreparing() {
    return !!this.user?.is_compliance_trackings_preparing;
  }

  get userHasTwoFactorAuth(): boolean {
    return !!this.getUser?.two_factor_auth.google;
  }

  get isIndividualOrHas2FA(): boolean {
    const user = this.getUser;
    const has2fa = !!user?.two_factor_auth.google;
    const isIndividual = user?.company?.legal_type === LegalType.INDIVIDUAL;

    return has2fa || isIndividual;
  }

  get isLimitedFeaturesFlexPsp(): boolean {
    return !!this.getUserCompany?.company_types.includes(
      CompanyType.LIMITED_FEATURES_FLEXPSP
    );
  }

  get isCompanyIndividual(): boolean {
    return LegalType.INDIVIDUAL === this.getUser?.company?.legal_type;
  }

  get isCompanyBusiness(): boolean {
    return LegalType.COMPANY === this.getUser?.company?.legal_type;
  }

  get isUserCompanyEnabled(): boolean {
    return !!this.getUserCompany?.enabled;
  }

  get hasUser(): boolean {
    return null !== this.user;
  }

  get userRoleSlugs(): UserRole[] | undefined {
    return this.user?.roles?.map((role) => role.slug);
  }

  get hasUserRoleSlug() {
    return (role: UserRole): boolean => {
      const userRoleSlugs = this.userRoleSlugs;
      if (!userRoleSlugs) {
        return false;
      }

      return !!~userRoleSlugs.indexOf(role);
    };
  }

  get isUserPortalServiceAttached() {
    return (portalService: PortalService): boolean => {
      return !!this.user?.portal_services[portalService.value]?.is_attached;
    };
  }

  get getPortalServicesAttached(): PortalService[] {
    return this.context.getters.getPortalServices.filter(
      this.isUserPortalServiceAttached
    );
  }

  get getPortalServicesAllowed(): PortalService[] {
    return this.context.getters.getPortalServices.filter(
      this.isUserPortalServiceAllowed
    );
  }

  get isUserPortalServiceAllowed() {
    return (portalService: PortalService): boolean => {
      return !!this.user?.portal_services[portalService.value]?.is_allowed;
    };
  }

  get isUserCompanyPortalServiceAttached() {
    return (portalService: PortalService): boolean => {
      return !!this.user?.company?.portal_services[portalService.value]
        ?.is_attached;
    };
  }

  get isUserCompanyPortalServiceAllowed() {
    return (portalService: PortalService): boolean => {
      return !!this.user?.company?.portal_services[portalService.value]
        ?.is_allowed;
    };
  }

  get isRoleSuperAdmin(): boolean {
    return this.hasUserRoleSlug(UserRole.SUPER_ADMIN);
  }

  get profileRoute(): object {
    if (this.isManager) {
      return {
        name: "manager.profile",
      };
    }

    return {
      name: "admin.profile",
    };
  }

  get isRoleAdmin(): boolean {
    return this.hasUserRoleSlug(UserRole.ADMIN);
  }

  get isRoleManagerReader(): boolean {
    return this.hasUserRoleSlug(UserRole.MANAGER_READER);
  }

  get isRoleManager(): boolean {
    return this.hasUserRoleSlug(UserRole.MANAGER);
  }

  get isUserLoggedByAdmin(): boolean {
    return !!this.adminBearerToken?.access_token;
  }

  get isAdmin(): boolean {
    return this.isRoleSuperAdmin || this.isRoleAdmin || false;
  }

  get hasAffiliates(): boolean {
    return this.getAffiliatesCount > 0;
  }

  get getAffiliatesCount(): number {
    return this.user?.affiliates.count || 0;
  }

  get isManager(): boolean {
    return this.isRoleManager || this.isRoleManagerReader || false;
  }

  get isAffiliated(): boolean {
    return null !== this.getUserCompany?.affiliate_owner_company;
  }

  @Mutation
  [Mutations.COMMON_AUTH_SET](token: BearerToken) {
    JwtService.saveToken(token);
    this.bearerToken = token;
  }

  @Mutation
  [Mutations.COMMON_AUTH_EXPIRES_REMINDER_SET_INTERVAL](
    interval: number | null
  ) {
    if (this.authExpiresInterval) {
      clearInterval(this.authExpiresInterval);
    }

    this.authExpiresInterval = interval;
  }

  @Mutation
  [Mutations.COMMON_AUTH_ADMIN_TOKEN_SET](token: BearerToken) {
    JwtService.saveAdminToken(token);
    this.adminBearerToken = token;
  }

  @Mutation
  [Mutations.COMMON_AUTH_PURGE]() {
    JwtService.destroyToken();
    UserService.destroyUser();
    this.bearerToken = null;
    this.user = null;
  }

  @Mutation
  [Mutations.COMMON_AUTH_ADMIN_TOKEN_PURGE]() {
    JwtService.destroyAdminToken();
    this.adminBearerToken = null;
  }

  @Mutation
  [Mutations.COMMON_COMPANY_ALERTS_SET](alerts: CompanyAlert[]) {
    this.company_alerts = alerts;
  }

  @Action({ rawError: true })
  async [Actions.COMMON_USER_PULL]() {
    return ApiService.common.profile
      .view()
      .then(ApiService.unwrapItemResponse<User>)
      .then((user) => {
        UserService.saveUser(user);
        this.context.commit(Mutations.COMMON_USER_SET, user);

        return user;
      })
      .catch(console.log);
  }

  @Mutation
  [Mutations.COMMON_USER_SET](user: User) {
    UserService.saveUser(user);
    this.user = Object.assign(this.user || {}, user);
  }

  @Action
  async [Actions.COMMON_USER_COMPANY_SET](company: Company) {
    const user = this.user;
    if (!user) {
      return;
    }
    user.company = Object.assign(user.company || {}, company);

    return this.context.commit(Mutations.COMMON_USER_SET, user);
  }

  @Action({ rawError: true })
  async [Actions.COMMON_AUTH_RETURN_TO_ADMIN]() {
    this.context.commit(Mutations.COMMON_AUTH_SET, JwtService.getAdminToken());

    return this.context
      .dispatch(Actions.COMMON_USER_PULL)
      .then(() => {
        this.context.commit(Mutations.COMMON_AUTH_ADMIN_TOKEN_PURGE);
      })
      .then(() => {
        return router.push({ name: "admin.users" });
      });
  }

  @Action({ rawError: true })
  async [Actions.COMMON_AUTH_LOGIN_AS_USER](user_id: number) {
    return ApiService.admin.user
      .loginByUser(user_id)
      .then(ApiService.unwrapItemResponse<LoginResponse>)
      .then(async (response: LoginResponse) => {
        this.context.commit(
          Mutations.COMMON_AUTH_ADMIN_TOKEN_SET,
          this.getBearerToken
        );
        store.commit(Mutations.MANAGER_COMPANY_ACCOUNTS_RESET);

        return this.context.dispatch(Actions.COMMON_AUTH_INSTALL, response);
      })
      .then(function () {
        const route = StorageService.popFromStorage("redirect_after_login");
        console.log(route);
        if (route) {
          return router.push(route as RouteLocationRaw);
        }

        return router.push(getDashboardRoute());
      })
      .then(() => this.context.dispatch(Actions.MANAGER_COMPANY_ACCOUNTS_LOAD));
  }

  @Action({ rawError: true })
  async [Actions.COMMON_AUTH_LOGIN](credentials: Credentials) {
    return ApiService.common.auth
      .login(credentials)
      .then(ApiService.unwrapItemResponse<LoginResponse>)
      .then(async (response: LoginResponse) => {
        store.commit(Mutations.MANAGER_COMPANY_ACCOUNTS_RESET);

        return this.context
          .dispatch(Actions.COMMON_AUTH_INSTALL, response)
          .then(() => response);
      })
      .then(function (response: LoginResponse) {
        console.log(response);
        if (response.redirect_url) {
          return window.location.assign(response.redirect_url);
        }
        const route = StorageService.popFromStorage("redirect_after_login");
        console.log(route);
        if (route) {
          return router.push(route as RouteLocationRaw);
        }

        return router.push(getDashboardRoute());
      });
  }

  @Action({ rawError: true })
  async [Actions.COMMON_AUTH_IFRAME_LOGIN](credentials: IFrameCredentials) {
    return ApiService.common.auth
      .loginIframe(credentials)
      .then(ApiService.unwrapItemResponse<LoginResponse>)
      .then(async (response: LoginResponse) => {
        store.commit(Mutations.MANAGER_COMPANY_ACCOUNTS_RESET);

        return this.context
          .dispatch(Actions.COMMON_AUTH_INSTALL, response)
          .then(() => response);
      });
  }

  @Action({ rawError: true })
  async [Actions.COMMON_AUTH_REGISTER](registration: Registration) {
    return ApiService.common.auth
      .registration(registration)
      .then(ApiService.unwrapItemResponse<LoginResponse>)
      .then((response: LoginResponse) => {
        return this.context.dispatch(Actions.COMMON_AUTH_INSTALL, response);
      });
  }

  @Action({ rawError: true })
  async [Actions.COMMON_AUTH_REFRESH_USER]() {
    return ApiService.common.profile
      .view()
      .then(ApiService.unwrapItemResponse)
      .then((response) => {
        this.context.commit(Mutations.COMMON_USER_SET, response);
      });
  }

  @Action({ rawError: true })
  async [Actions.MANAGER_COMPANY_ALERTS_LOAD]() {
    return ApiService.manager.company
      .alerts()
      .then(ApiService.unwrapItemsResponse<CompanyAlert>)
      .then((response) => {
        this.context.commit(Mutations.COMMON_COMPANY_ALERTS_SET, response);
      });
  }

  @Action({ rawError: true })
  async [Actions.COMMON_USER_UPDATE](data: User) {
    return ApiService.common.profile
      .update(data)
      .then(ApiService.unwrapItemResponse)
      .then((userData) => {
        this.context.commit(Mutations.COMMON_USER_SET, userData);
      });
  }

  @Action({ rawError: true })
  async [Actions.COMMON_USER_PASSWORD_UPDATE](data: ChangePassword) {
    return ApiService.common.profile
      .updatePassword(data)
      .then(ApiService.unwrapItemResponse)
      .then((userData) => {
        this.context.commit(Mutations.COMMON_USER_SET, userData);
      });
  }

  @Action({ rawError: true })
  async [Actions.COMMON_USER_TFA_REMOVE]() {
    return ApiService.common.two_factor_auth.google
      .remove()
      .then(ApiService.unwrapItemResponse)
      .then((userData) => {
        this.context.commit(Mutations.COMMON_USER_SET, userData);
      });
  }

  @Action({ rawError: true })
  async [Actions.COMMON_USER_TFA_SET](params: {
    secret: string;
    code: string;
  }) {
    return ApiService.common.two_factor_auth.google
      .assign(params)
      .then(ApiService.unwrapItemResponse)
      .then((userData) => {
        this.context.commit(Mutations.COMMON_USER_SET, userData);
      });
  }

  @Action({ rawError: true })
  async [Actions.COMMON_USER_AVATAR_UPDATE](file) {
    return ApiService.common.profile
      .updateAvatar(file)
      .then(ApiService.unwrapItemResponse)
      .then((profileData) => {
        this.context.commit(Mutations.COMMON_USER_SET, profileData);
      });
  }

  @Action({ rawError: true })
  async [Actions.COMMON_AUTH_LOGIN_FROM_LEGACY](key: string) {
    return ApiService.common.auth
      .loginFromLegacy(key)
      .then(ApiService.unwrapItemResponse<LoginResponse>)
      .then((response: LoginResponse) => {
        return this.context.dispatch(Actions.COMMON_AUTH_INSTALL, response);
      });
  }

  @Action({ rawError: true })
  async [Actions.COMMON_AUTH_LOGIN_FROM_FLEX_PSP]({ user_id, auth_key }) {
    return ApiService.common.auth
      .loginFromFlexPsp(user_id, auth_key)
      .then(ApiService.unwrapItemResponse<LoginResponse>)
      .then((response: LoginResponse) => {
        return this.context.dispatch(Actions.COMMON_AUTH_INSTALL, response);
      });
  }

  @Action({ rawError: true })
  async [Actions.COMMON_AUTH_LOGIN_FROM_REMOTE_PORTAL]({ portal, key }) {
    return ApiService.common.auth
      .loginFromRemotePortal(portal, key)
      .then(ApiService.unwrapItemResponse<LoginResponse>)
      .then(async (response: LoginResponse) => {
        return this.context.dispatch(Actions.COMMON_AUTH_INSTALL, response);
      });
  }

  @Action({ rawError: true })
  async [Actions.COMMON_AUTH_APPROVE_EMAIL]({ key, code }) {
    return ApiService.common.auth
      .approveEmail(key, code)
      .then(ApiService.unwrapItemResponse<LoginResponse>)
      .then(async (response: LoginResponse) => {
        store.commit(Mutations.MANAGER_COMPANY_ACCOUNTS_RESET);

        return this.context.dispatch(Actions.COMMON_AUTH_INSTALL, response);
      });
  }

  @Action({ rawError: true })
  async [Actions.COMMON_AUTH_GET_AFFILIATE_OWNER](key: string) {
    return ApiService.common.auth
      .affiliateOwnerByGuest(key)
      .then(ApiService.unwrapItemResponse);
  }

  @Action({ rawError: true })
  async [Actions.COMMON_AUTH_LOGIN_TO_LEGACY]() {
    return ApiService.common.auth
      .loginToLegacy()
      .then(ApiService.unwrapItemResponse<{ url: string }>)
      .then((response) => {
        if (!("url" in response)) {
          return null;
        }
        this.context.commit(Mutations.COMMON_AUTH_PURGE);

        return window.location.assign(response.url);
      })
      .catch(console.error);
  }

  @Action({ rawError: true })
  async [Actions.COMMON_AUTH_LOGOUT](request?: { withRedirect?: boolean }) {
    const withRedirect =
      request && "withRedirect" in request ? request.withRedirect : true;
    console.log("logout", { withRedirect });
    if (!this.getUser) {
      if (withRedirect) {
        await router.push({ name: "login" });
      }

      return Promise.resolve();
    }

    return await ApiService.common.auth
      .logout()
      .catch((e) => console.error(e))
      .finally(async () => {
        this.context.commit(Mutations.COMMON_AUTH_PURGE);
        await this.context.dispatch(Actions.COMMON_RESET);
        StorageService.purgeStorage();
        StorageService.purgeStorage("localStorage", StorageKey.USER_SETTINGS);

        if (withRedirect) {
          await router.push({ name: "login" });
        }

        return this.context.dispatch(Actions.COMMON_WEBSOCKETS_INITIATE);
      });
  }

  @Action({ rawError: true })
  async [Actions.COMMON_AUTH_PASSWORD_FORGOT](payload: ForgotPassword) {
    return ApiService.common.auth
      .forgotPassword(payload)
      .then(ApiService.unwrapItemResponse<{ message: string }>)
      .then((response) => response?.message);
  }

  @Action({ rawError: true })
  async [Actions.COMMON_AUTH_PASSWORD_RESET]({ user_id, code, data }) {
    return ApiService.common.auth
      .resetPassword(user_id, code, data)
      .then(ApiService.unwrapItemResponse<{ message: string }>)
      .then((response) => response?.message);
  }

  @Action({ rawError: true })
  async [Actions.COMMON_AUTH_VERIFY]() {
    if (!JwtService.getToken()) {
      this.context.commit(Mutations.COMMON_AUTH_PURGE);

      return;
    }
    if (!this.isTokenRefreshable || !this.isTokenRequiresRefresh) {
      return;
    }

    return this.context.dispatch(Actions.COMMON_AUTH_REFRESH);
  }

  @Action({ rawError: true })
  async [Actions.COMMON_AUTH_EXPIRES_REMINDER_SET]() {
    this.context.commit(
      Mutations.COMMON_AUTH_EXPIRES_REMINDER_SET_INTERVAL,
      null
    );

    const interval = window.setInterval(() => {
      const expiresTime = this.bearerTokenExpiresDate?.getTime();
      const nowTime = new Date().getTime();
      if (!expiresTime) {
        return this.context.commit(
          Mutations.COMMON_AUTH_EXPIRES_REMINDER_SET_INTERVAL,
          null
        );
      }
      const expiresInMs = expiresTime - nowTime;
      const expiresInSeconds = Math.floor(expiresInMs / 1000);

      if (expiresInSeconds > timeout) {
        return;
      }
      this.context.commit(
        Mutations.COMMON_AUTH_EXPIRES_REMINDER_SET_INTERVAL,
        null
      );
      /*try {
        const audio = new Audio("/media/sound/notification.mp3");
        audio.play();
      } catch (e) {
        console.error(e);
      }*/
      swal
        .fire({
          timer: expiresInMs,
          title: `Your Session is About to Expire`,
          confirmButtonText: "Stay Logged In",
        })
        .then((result: SweetAlertResult) => {
          if (!result.isConfirmed) {
            this.context.dispatch(Actions.COMMON_AUTH_LOGOUT);

            return;
          }
          this.context.dispatch(Actions.COMMON_AUTH_REFRESH);
        });
    }, 5000);
    this.context.commit(
      Mutations.COMMON_AUTH_EXPIRES_REMINDER_SET_INTERVAL,
      interval
    );
  }

  @Action({ rawError: true })
  async [Actions.COMMON_AUTH_INSTALL](response: LoginResponse) {
    this.context.commit(Mutations.COMMON_AUTH_SET, response.token);
    this.context.commit(Mutations.COMMON_USER_SET, response.user);

    await this.context.dispatch(Actions.COMMON_AUTH_EXPIRES_REMINDER_SET);
    await this.context.dispatch(Actions.THEME_LIVECHAT_INIT);

    console.log("release_version", process.env.VUE_APP_RELEASE);
    if (process.env.VUE_APP_RELEASE) {
      window.localStorage.setItem(
        "release_version",
        process.env.VUE_APP_RELEASE
      );
    }

    return this.context.dispatch(Actions.COMMON_WEBSOCKETS_INITIATE);
  }

  @Action({ rawError: true })
  async [Actions.COMMON_AUTH_REFRESH]() {
    if (!JwtService.getToken()) {
      this.context.commit(Mutations.COMMON_AUTH_PURGE);

      return;
    }
    if (!this.isTokenRefreshable) {
      return;
    }

    return ApiService.common.auth
      .refresh()
      .then(ApiService.unwrapItemResponse<LoginResponse>)
      .then(async (response: LoginResponse) => {
        return this.context.dispatch(Actions.COMMON_AUTH_INSTALL, response);
      })
      .catch(() => {
        this.context.commit(Mutations.COMMON_AUTH_PURGE);
      });
  }
}
